import { BigNumber } from '@ethersproject/bignumber';
import { FC, ReactNode, useMemo } from 'react';
import { AssetMenu } from './AssetMenu';
import { HealthFactor, GetNewLTV, HealthFactorConfig } from '../HealthFactor';
import { MaxAmountInput } from '../MaxAmountInput';
import { AmountInput } from '../AmountInput';
import { ActionFormContext } from './ActionForm.context';
import { Approval, ApprovalProps } from './Approval';
import { UseActionFormProps, useActionForm } from './useActionForm';
import { useAmountInUSD } from './useAmountInUSD';
import { useThisDecoratedSilo } from '../../silo';
import { getAssetsHash, SiloAsset } from 'queries/decoratedSilo';
import { F } from 'lodash/fp';
import { SubmissionModalContext } from './SubmissionModal.context';
import { GetAverageAPY, HistoricalApyChart, MapAPYData } from './ActionFormCharts';
import { BIG_ZERO } from '../../../../constants';
import { ErrorBoundary } from 'react-error-boundary';
import { useIsChartsFeatureOn } from './useIsChartsFeatureOn';
import { asFullUSD, isUndefined } from '@silo/utils';
import { useSiloWeb3 } from '@silo/web3';
import { useIsCurveLpAsset } from 'app/hooks/useCurveLpAsset';
import { TermsOfUseModal } from '../../terms-of-use';
import { useQueryTermsOfUseAgreement } from 'app/queries/terms-of-use';
import { TermsOfUseAction } from 'data/terms-of-use/types';
import { AMOUNT_ZERO, makeAmount, ProtocolInstance } from '@silo/types';
import { Button, Modal, useBreakpoints, useModalState } from '@silo/ui';
import { Option, pipe } from 'effect';

type RequireApprovalProps = Pick<ApprovalProps, 'queryApproval' | 'doApprove'>;

export interface ActionFormProps extends UseActionFormProps {
  protocolInstance: ProtocolInstance;
  maxAmountSlot?: ReactNode;
  maxLabelSlot?: ReactNode;
  postAmountSlot?: ReactNode;
  postMaxSlot?: ReactNode;
  preSubmitSlot?: ReactNode;
  getNewLTV: GetNewLTV;
  submitButtonContentSlot: (params: { selectedAsset: SiloAsset }) => ReactNode;
  preChartPanels?: ReactNode[];
  postChartPanels?: ReactNode[];
  requireApproval?: RequireApprovalProps;
  customForm?: {
    condition: CustomFormCondition;
    slot: ReactNode;
  };
  healthFactorConfig?: HealthFactorConfig;
  assetsToDisplay?: SiloAsset[];
  aprChartConfig?: {
    getAverageAPY: GetAverageAPY;
    mapAPYData?: MapAPYData;
  };
  'data-cy'?: string;
  termsOfUseAction: TermsOfUseAction;
  utilizationChart?: ReactNode;
}

export type CustomFormCondition = (params: { selectedAsset: SiloAsset }) => boolean;

export const ActionForm: FC<ActionFormProps> = ({ assetsToDisplay, ...rest }) => {
  const decoratedSilo = useThisDecoratedSilo();
  const assets = assetsToDisplay ?? decoratedSilo.assets;
  const assetsHash = getAssetsHash(assets);

  // reset action form whenever the assets change
  return <BaseActionForm key={assetsHash} assetsToDisplay={assetsToDisplay} {...rest} />;
};

const BaseActionForm: FC<ActionFormProps> = ({
  protocolInstance,
  maxAmountSlot,
  maxLabelSlot,
  postAmountSlot,
  postMaxSlot,
  preSubmitSlot,
  getNewLTV,
  submitButtonContentSlot,
  preChartPanels = [],
  postChartPanels = [],
  requireApproval,
  assetsToDisplay,
  'data-cy': dataCy,
  customForm = { condition: F, slot: <></> },
  healthFactorConfig,
  submissionModalContentSlot,
  aprChartConfig,
  getNewUtilization,
  termsOfUseAction,
  utilizationChart,
  ...useActionFormProps
}) => {
  const decoratedSilo = useThisDecoratedSilo();
  const isChartsTabOn = useIsChartsFeatureOn();
  const { sm } = useBreakpoints();
  const { isConnected } = useSiloWeb3();

  const {
    tokenAmount,
    tokenAmountRef,
    normalizedTokenAmount,
    setTokenAmount,
    resetTokenAmount,
    maxAmount,
    setMaxAmount,
    selectedAddress,
    selectedAsset,
    setSelectedAddress,
    currentLTV,
    isFullAmount,
    utilizationImpact,
    isInvalid,
    canSubmit,
    setPreventSubmit,
    errorMessage,
    submitButtonErrorMessage,
    submit,
    isPending,
    isSubmissionModalOpen,
    submissionModalContext,
  } = useActionForm({
    protocolInstance,
    assetsToDisplay,
    submissionModalContentSlot,
    getNewUtilization,
    ...useActionFormProps,
  });

  const isCurveLpAsset = useIsCurveLpAsset(decoratedSilo.siloAddress, selectedAsset.assetKey);
  const submitButtonContent = submitButtonErrorMessage || submitButtonContentSlot({ selectedAsset });
  const assetMenuAssets = assetsToDisplay ?? decoratedSilo.assets;

  const amount = pipe(
    normalizedTokenAmount,
    Option.fromNullable,
    Option.map((bn) => bn.toBigInt()),
    Option.map(makeAmount),
    Option.getOrElse(() => AMOUNT_ZERO)
  );

  const actionFormContextValue: ActionFormContext = {
    assetMenuAssets,
    selectedAddress,
    setSelectedAddress,
    selectedAsset,
    normalizedTokenAmount,
    setTokenAmount,
    resetTokenAmount,
    isPending,
    maxAmount,
    isFullAmount,
    utilizationImpact,
    currentLTV,
    amount,
  };

  const showCustomForm = useMemo(() => customForm.condition({ selectedAsset }), [customForm, selectedAsset]);

  if (showCustomForm) {
    return (
      <ActionFormContext.Provider value={actionFormContextValue}>
        <ActionPanels panels={[customForm.slot]} />
      </ActionFormContext.Provider>
    );
  }

  const showMaxAmountSlot = isConnected && !!maxAmountSlot;
  const showMaxAmountInput = isConnected && !maxAmountSlot;

  const mainPanel = (
    <div className="flex flex-col gap-5" data-cy={dataCy}>
      {!sm && (
        <AssetMenu
          protocolInstance={protocolInstance}
          assets={assetMenuAssets}
          selectedAddress={selectedAddress}
          setSelectedAddress={setSelectedAddress}
          onSelect={resetTokenAmount}
          disabled={isPending}
          dataCyPrefix={dataCy}
          asButton
        />
      )}
      <div
        className={`p-2.5 sm:p-5 rounded-lg ${isPending ? 'bg-shale-750' : 'bg-shale-700'} ${
          errorMessage ? 'outline outline-red-400 outline-2' : ''
        }`}
      >
        <div className="flex gap-5 items-center">
          {sm && (
            <>
              <AssetMenu
                protocolInstance={protocolInstance}
                assets={assetMenuAssets}
                selectedAddress={selectedAddress}
                setSelectedAddress={setSelectedAddress}
                onSelect={resetTokenAmount}
                disabled={isPending}
                dataCyPrefix={dataCy}
              />
              <div className="self-stretch w-px bg-shale-500" />
            </>
          )}
          <AmountInput
            inputVal={tokenAmount}
            setInputVal={setTokenAmount}
            ref={tokenAmountRef}
            decimals={selectedAsset.decimals}
            disabled={isPending}
          />
          <AmountInUSD selectedAsset={selectedAsset} normalizedAmount={normalizedTokenAmount} disabled={isPending} />
        </div>
      </div>

      {errorMessage && (
        <p className="text-base text-red-400" data-cy="form-error-message">
          {errorMessage}
        </p>
      )}

      {postAmountSlot ? postAmountSlot : null}

      {showMaxAmountSlot && <div>{maxAmountSlot}</div>}

      {showMaxAmountInput && (
        <MaxAmountInput
          max={maxAmount}
          doMax={setMaxAmount}
          maxLabelSlot={maxLabelSlot}
          disabled={isPending}
          symbol={selectedAsset.symbol}
        />
      )}

      {postMaxSlot ? postMaxSlot : null}

      {currentLTV && (
        <div>
          <HealthFactor
            variant="dual"
            normalizedTokenAmount={normalizedTokenAmount}
            currentLTV={currentLTV}
            getNewLTV={getNewLTV}
            selectedAsset={selectedAsset}
            decoratedSilo={decoratedSilo}
            setTokenAmount={setTokenAmount}
            setMaxAmount={setMaxAmount}
            healthFactorConfig={healthFactorConfig}
            disabled={isPending}
          />
        </div>
      )}

      {preSubmitSlot ? preSubmitSlot : null}

      <div className="flex flex-col gap-5">
        {requireApproval && (
          <Approval
            selectedAsset={selectedAsset}
            setPreventSubmit={setPreventSubmit}
            hide={isInvalid}
            normalizedTokenAmount={normalizedTokenAmount}
            {...requireApproval}
            protocolInstance={protocolInstance}
          />
        )}

        <SubmitButton
          submit={submit}
          canSubmit={canSubmit}
          submitButtonContent={submitButtonContent}
          protocolInstance={protocolInstance}
          termsOfUseAction={termsOfUseAction}
        />

        <Modal isOpen={isSubmissionModalOpen} closeModal={submissionModalContext.cancel}>
          <SubmissionModalContext.Provider value={submissionModalContext}>
            {submissionModalContentSlot}
          </SubmissionModalContext.Provider>
        </Modal>
      </div>
    </div>
  );

  const getChartPanels = (): ReactNode[] => {
    if (isCurveLpAsset) return [];

    if (!isChartsTabOn || isUndefined(aprChartConfig)) return [];

    return [
      ...(!!utilizationChart ? [utilizationChart] : []),
      <HistoricalApyChart
        asset={selectedAsset}
        siloAddress={decoratedSilo.siloAddress}
        mapAPYData={aprChartConfig.mapAPYData}
        getAverageAPY={aprChartConfig.getAverageAPY}
      />,
    ];
  };

  const panels = [mainPanel, ...preChartPanels, ...getChartPanels(), ...postChartPanels];

  return (
    <ActionFormContext.Provider value={actionFormContextValue}>
      <ActionPanels panels={panels} />
    </ActionFormContext.Provider>
  );
};

export const ActionPanels: FC<{ panels: ReactNode[] }> = ({ panels }) => (
  <div className="flex flex-col gap-0.5">
    {panels.map((panel, index) => (
      <ErrorBoundary
        fallback={
          <div className="p-4 bg-shale-800">
            A system error has occured. Please contact us for assistance if the error persists.
          </div>
        }
        key={index}
      >
        <div className="px-5 py-5 first:pt-0 last:rounded-b-lg bg-shale-800">{panel}</div>
      </ErrorBoundary>
    ))}
  </div>
);

interface AmountInUSDProps {
  selectedAsset: SiloAsset;
  normalizedAmount: BigNumber | null;
  disabled?: boolean;
}

export const AmountInUSD: FC<AmountInUSDProps> = ({ selectedAsset, normalizedAmount, disabled = false }) => {
  const { amountInUSD } = useAmountInUSD({ selectedAsset, normalizedAmount });

  return (
    <span
      className={`font-mono text-base font-normal text-shale-300 ${disabled ? 'opacity-70' : ''}`}
      data-cy="amount-in-usd"
    >
      {asFullUSD(amountInUSD ?? BIG_ZERO)}
    </span>
  );
};

interface SubmitButtonProps {
  submit: () => void;
  canSubmit: boolean;
  submitButtonContent: ReactNode;
  protocolInstance: ProtocolInstance;
  termsOfUseAction: TermsOfUseAction;
}

function SubmitButton({
  submit,
  canSubmit,
  submitButtonContent,
  protocolInstance,
  termsOfUseAction,
}: SubmitButtonProps) {
  const { account } = useSiloWeb3();
  const { hasAgreed } = useQueryTermsOfUseAgreement();
  const { isModalOpen, closeModal, openModal } = useModalState();
  const { siloAddress } = useThisDecoratedSilo();

  const onClick = () => {
    if (hasAgreed) {
      submit();
    } else {
      openModal();
    }
  };

  return (
    <div>
      <Button variant="primary" className="w-full" onClick={onClick} disabled={!canSubmit} data-cy="submit-button">
        {submitButtonContent}
      </Button>

      {!!account && (
        <TermsOfUseModal
          isModalOpen={isModalOpen}
          closeModal={closeModal}
          user={account}
          onSuccess={() => submit()}
          market={siloAddress}
          action={termsOfUseAction}
          protocolInstance={protocolInstance}
        />
      )}
    </div>
  );
}
